import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  CardContent,
  CardHeader,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
  Slider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  OpenInFull as ResizeIcon
} from '@mui/icons-material';
import {
  WalteroCard,
  WalteroButton,
  WalteroStatusChip,
  WalteroTypography
} from './waltero';
import { WalteroChart, WALTERO_CHART_COLORS, getChartColor } from './waltero';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import telemetryService from '../services/telemetryService';

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30000, // 30 seconds
      cacheTime: 300000, // 5 minutes
    },
  },
});

/**
 * TelemetryTab - Dynamic sensor data visualization with ECharts
 */
function TelemetryTab({ selectedOrgId }) {
  return (
    <QueryClientProvider client={queryClient}>
      <TelemetryTabContent selectedOrgId={selectedOrgId} />
    </QueryClientProvider>
  );
}

function TelemetryTabContent({ selectedOrgId }) {
  // State for chart configuration
  const [charts, setCharts] = useState([
    {
      id: 1,
      title: 'Temperature & Battery Voltage',
      selectedSensors: [],
      selectedMeasures: ['temperature_1', 'battery_voltage'],
      timeRange: '24h',
      width: 650,
      height: 650
    }
  ]);
  

  // Fetch available measures
  const { data: measuresData, isLoading: measuresLoading, error: measuresError } = useQuery({
    queryKey: ['measures'],
    queryFn: () => telemetryService.getAvailableMeasures(),
    enabled: true
  });

  // Fetch available sensors for selected organization
  const { data: sensorsData, isLoading: sensorsLoading, error: sensorsError } = useQuery({
    queryKey: ['sensors', selectedOrgId],
    queryFn: () => telemetryService.getAvailableSensors(selectedOrgId),
    enabled: !!selectedOrgId
  });

  const availableMeasures = useMemo(() => {
    return measuresData?.measures || {};
  }, [measuresData]);

  const availableSensors = useMemo(() => {
    const sensors = sensorsData?.sensors || [];
    // Sort sensors by serial number
    return sensors.sort((a, b) => {
      const serialA = String(a.serialNumber || '').trim();
      const serialB = String(b.serialNumber || '').trim();
      return serialA.localeCompare(serialB, undefined, { numeric: true });
    });
  }, [sensorsData]);

  // Helper function to generate ECharts configuration with Waltero theming
  const generateChartConfig = (chartData, chart, deviceMetadata) => {
    if (!chartData || !chart.selectedMeasures.length) {
      return {};
    }

    // Generate series for each measure-device combination
    const series = [];
    const legend = [];
    const yAxes = [];

    // Track which y-axis index each measure uses
    const measureYAxisMap = {};
    const measureDataRanges = {}; // Track actual data ranges for each measure
    let yAxisIndex = 0;
    let colorIndex = 0;

    // First pass: collect data ranges for each measure
    chart.selectedMeasures.forEach(measureKey => {
      if (chartData[measureKey]) {
        let minVal = Infinity;
        let maxVal = -Infinity;

        Object.values(chartData[measureKey]).forEach(deviceData => {
          deviceData.forEach(point => {
            const value = point[1];
            if (value !== null && value !== undefined && !isNaN(value)) {
              minVal = Math.min(minVal, value);
              maxVal = Math.max(maxVal, value);
            }
          });
        });

        if (minVal !== Infinity && maxVal !== -Infinity) {
          // Add 10% padding to the range for better visualization
          const range = maxVal - minVal;
          const padding = range > 0 ? range * 0.1 : Math.abs(maxVal) * 0.1 || 1;

          measureDataRanges[measureKey] = {
            min: minVal - padding,
            max: maxVal + padding
          };
        }
      }
    });

    chart.selectedMeasures.forEach(measureKey => {
      const measureInfo = availableMeasures[measureKey];
      if (!measureInfo) return;

      // Create y-axis for this measure if not exists
      if (!(measureKey in measureYAxisMap)) {
        measureYAxisMap[measureKey] = yAxisIndex;

        // Use actual data range if available, otherwise fall back to configured limits
        const dataRange = measureDataRanges[measureKey];
        const yAxisConfig = {
          type: 'value',
          name: `${measureInfo.name} (${measureInfo.unit})`,
          position: yAxisIndex % 2 === 0 ? 'left' : 'right',
          axisLabel: {
            formatter: `{value} ${measureInfo.unit}`
          }
        };

        // Only set min/max if we have data or configured values
        if (dataRange) {
          yAxisConfig.min = dataRange.min;
          yAxisConfig.max = dataRange.max;
        } else if (measureInfo.minValue !== undefined || measureInfo.maxValue !== undefined) {
          // Use configured values as fallback
          if (measureInfo.minValue !== undefined) yAxisConfig.min = measureInfo.minValue;
          if (measureInfo.maxValue !== undefined) yAxisConfig.max = measureInfo.maxValue;
        }
        // If neither data range nor configured values, let ECharts auto-scale

        yAxes.push(yAxisConfig);
        yAxisIndex++;
      }

      // Add series for each device for this measure
      if (chartData[measureKey]) {
        Object.keys(chartData[measureKey]).forEach(deviceId => {
          const deviceInfo = deviceMetadata?.[deviceId];
          const seriesName = `${deviceInfo?.serialNumber || deviceId} - ${measureInfo.name}`;
          const seriesColor = getChartColor(colorIndex, WALTERO_CHART_COLORS);
          
          series.push({
            name: seriesName,
            type: 'line',
            yAxisIndex: measureYAxisMap[measureKey],
            data: chartData[measureKey][deviceId] || [],
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            lineStyle: {
              width: 2,
              color: seriesColor
            },
            itemStyle: {
              color: seriesColor
            },
            emphasis: {
              focus: 'series',
              lineStyle: {
                width: 3
              }
            }
          });
          
          legend.push(seriesName);
          colorIndex++;
        });
      }
    });

    return {
      title: {
        text: chart.title,
        left: 'center'
      },
      
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          animation: false
        },
        formatter: (params) => {
          if (!params.length) return '';
          
          const time = new Date(params[0].value[0]).toLocaleString();
          let content = `<strong>${time}</strong><br/>`;
          
          params.forEach(param => {
            const value = param.value[1];
            const measureKey = chart.selectedMeasures.find(m => param.seriesName.includes(availableMeasures[m]?.name));
            const unit = measureKey ? availableMeasures[measureKey]?.unit || '' : '';
            content += `${param.marker} ${param.seriesName}: <strong>${value} ${unit}</strong><br/>`;
          });
          
          return content;
        }
      },
      
      legend: {
        data: legend,
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0
      },
      
      grid: {
        left: '10%',
        right: '10%',
        bottom: '20%',
        top: '15%',
        containLabel: true
      },
      
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          rotate: 45,
          formatter: (value) => {
            return new Date(value).toLocaleString();
          }
        }
      },
      
      yAxis: yAxes,
      
      series: series,
      
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          yAxisIndex: yAxes.map((_, index) => index), // Apply to all y-axes
          filterMode: 'filter'
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: '5%',
          filterMode: 'filter'
        }
      ],

      // Enable zoom reset on double click
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: {
              zoom: 'Zoom',
              back: 'Reset'
            }
          },
          restore: {
            title: 'Reset View'
          }
        },
        right: '10px',
        top: '10px'
      }
    };
  };

  // Add new chart
  const addChart = () => {
    const newChart = {
      id: Date.now(),
      title: `Chart ${charts.length + 1}`,
      selectedSensors: [],
      selectedMeasures: [],
      timeRange: '24h',
      width: 650,
      height: 650
    };
    setCharts([...charts, newChart]);
  };

  // Remove chart
  const removeChart = (chartId) => {
    setCharts(charts.filter(chart => chart.id !== chartId));
  };

  // Update chart configuration
  const updateChart = (chartId, updates) => {
    setCharts(charts.map(chart => 
      chart.id === chartId ? { ...chart, ...updates } : chart
    ));
  };

  if (!selectedOrgId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select an organization to view telemetry data visualizations.
        </Alert>
      </Box>
    );
  }

  if (measuresError || sensorsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load telemetry configuration: {measuresError?.message || sensorsError?.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <WalteroTypography variant="h4">
          Telemetry Visualization
        </WalteroTypography>
        <WalteroButton
          variant="primary"
          startIcon={<AddIcon />}
          onClick={addChart}
          disabled={charts.length >= 6} // Limit to 6 charts for performance
        >
          Add Chart
        </WalteroButton>
      </Box>


      {/* Charts */}
      <Grid container spacing={3}>
        {charts.map((chart) => (
          <ChartCard
            key={chart.id}
            chart={chart}
            availableMeasures={availableMeasures}
            availableSensors={availableSensors}
            onUpdate={(updates) => updateChart(chart.id, updates)}
            onRemove={() => removeChart(chart.id)}
            canRemove={charts.length > 1}
            generateChartConfig={generateChartConfig}
          />
        ))}
      </Grid>

      {charts.length === 0 && (
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary" gutterBottom>
            No charts configured
          </WalteroTypography>
          <WalteroButton variant="primary" startIcon={<AddIcon />} onClick={addChart}>
            Create Your First Chart
          </WalteroButton>
        </WalteroCard>
      )}
    </Box>
  );
}

/**
 * Individual Chart Card Component
 */
function ChartCard({ 
  chart, 
  availableMeasures, 
  availableSensors, 
  onUpdate, 
  onRemove, 
  canRemove,
  generateChartConfig
}) {
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef({ startX: 0, startY: 0, startWidth: 0, startHeight: 0 });

  const handleMouseDown = (e) => {
    e.preventDefault();
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: chart.width,
      startHeight: chart.height
    };
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    
    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;
    
    const newWidth = Math.max(400, resizeRef.current.startWidth + deltaX);
    const newHeight = Math.max(300, resizeRef.current.startHeight + deltaY);
    
    onUpdate({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);
  // Query chart data
  const { data: chartResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['chartData', chart.id, chart.selectedSensors, chart.selectedMeasures, chart.timeRange],
    queryFn: () => {
      if (!chart.selectedSensors.length || !chart.selectedMeasures.length) {
        return null;
      }
      
      return telemetryService.queryChartData({
        deviceIds: chart.selectedSensors.map(s => s.deviceId),
        measures: chart.selectedMeasures,
        timeRange: chart.timeRange,
        aggregation: 'raw',
        resolution: '1m',
        limit: 10000
      });
    },
    enabled: chart.selectedSensors.length > 0 && chart.selectedMeasures.length > 0,
    staleTime: 0
  });

  const chartOption = useMemo(() => {
    if (!chartResponse?.chartData) return {};
    return generateChartConfig(chartResponse.chartData, chart, chartResponse.metadata?.devices);
  }, [chartResponse, chart, generateChartConfig]);

  // Generate a key that changes when device/measure configuration changes to force chart re-mount
  const chartKey = useMemo(() => {
    const deviceIds = chart.selectedSensors.map(s => s.deviceId).sort().join(',');
    const measures = chart.selectedMeasures.sort().join(',');
    return `${chart.id}-${deviceIds}-${measures}`;
  }, [chart.id, chart.selectedSensors, chart.selectedMeasures]);

  const measureOptions = Object.entries(availableMeasures).map(([key, measure]) => ({
    value: key,
    label: `${measure.name} (${measure.unit})`,
    color: measure.color
  }));

  return (
    <Grid item xs={12} lg={6}>
      <Box
        sx={{ 
          width: `${chart.width}px`,
          height: `${chart.height}px`,
          position: 'relative',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          cursor: isResizing ? 'nw-resize' : 'default',
          userSelect: isResizing ? 'none' : 'auto'
        }}
      >
        <CardHeader
          title={
            <TextField
              value={chart.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              variant="standard"
              size="small"
              sx={{ '& .MuiInput-input': { fontSize: '1.25rem', fontWeight: 500 } }}
            />
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <WalteroButton size="small" variant="outlined" onClick={() => refetch()}>
                <RefreshIcon />
              </WalteroButton>
              {canRemove && (
                <WalteroButton size="small" variant="danger" onClick={onRemove}>
                  <DeleteIcon />
                </WalteroButton>
              )}
            </Box>
          }
        />
        
        <CardContent>
          {/* Chart Configuration */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} lg={8}>
              <Autocomplete
                multiple
                size="small"
                options={availableSensors}
                getOptionLabel={(option) => {
                  const serialNumber = String(option.serialNumber || '').trim();
                  const meterName = String(option.meterName || '').trim();
                  return meterName ? `${serialNumber} (${meterName})` : serialNumber;
                }}
                value={chart.selectedSensors}
                onChange={(e, newValue) => onUpdate({ selectedSensors: newValue })}
                renderInput={(params) => (
                  <TextField {...params} label="Select Sensors" placeholder="Choose sensors..." />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <WalteroStatusChip
                      label={String(option.serialNumber || '').trim()}
                      status="info"
                      size="small"
                      {...getTagProps({ index })}
                      key={option.deviceId}
                    />
                  ))
                }
                limitTags={3}
                sx={{ minWidth: '400px' }}
                ListboxProps={{
                  style: { minWidth: '500px' }
                }}
              />
            </Grid>
            
            <Grid item xs={12} lg={4}>
              <Autocomplete
                multiple
                size="small"
                options={measureOptions}
                getOptionLabel={(option) => option.label}
                value={measureOptions.filter(opt => chart.selectedMeasures.includes(opt.value))}
                onChange={(e, newValue) => onUpdate({ selectedMeasures: newValue.map(v => v.value) })}
                renderInput={(params) => (
                  <TextField {...params} label="Select Measures" placeholder="Choose measures..." />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <WalteroStatusChip
                      label={option.label}
                      status="active"
                      size="small"
                      sx={{ 
                        backgroundColor: option.color + '20', 
                        color: option.color,
                        borderColor: option.color
                      }}
                      {...getTagProps({ index })}
                      key={option.value}
                    />
                  ))
                }
                limitTags={2}
                sx={{ minWidth: '300px' }}
                ListboxProps={{
                  style: { minWidth: '400px' }
                }}
              />
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={chart.timeRange}
                  label="Time Range"
                  onChange={(e) => onUpdate({ timeRange: e.target.value })}
                >
                  <MenuItem value="1h">1 Hour</MenuItem>
                  <MenuItem value="6h">6 Hours</MenuItem>
                  <MenuItem value="24h">24 Hours</MenuItem>
                  <MenuItem value="7d">7 Days</MenuItem>
                  <MenuItem value="30d">30 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
          </Grid>

          {/* Chart Display */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load chart data: {error.message}
            </Alert>
          )}
          
          {!isLoading && !error && chartResponse && (
            <>
              {/* Data Truncation Warning */}
              {chartResponse.metadata?.dataTruncation?.truncatedFromStart && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Data truncated from beginning:</strong> {' '}
                  Due to payload size limits, {chartResponse.metadata.dataTruncation.truncatedRecords.toLocaleString()} oldest records were removed.
                  Showing {chartResponse.metadata.dataTruncation.returnedRecords.toLocaleString()} of {chartResponse.metadata.dataTruncation.originalRecords.toLocaleString()} records 
                  ({Math.round((chartResponse.metadata.dataTruncation.returnedRecords / chartResponse.metadata.dataTruncation.originalRecords) * 100)}% of data).
                  {chartResponse.metadata.dataTruncation.actualStartTime && (
                    <>
                      {' '}Data starts from {new Date(chartResponse.metadata.dataTruncation.actualStartTime).toLocaleString()} instead of the requested start time.
                    </>
                  )}
                  {' '}Consider using a shorter time range or data aggregation to see older data.
                </Alert>
              )}
              
              {Object.keys(chartOption).length > 0 ? (
                <WalteroChart
                  key={chartKey}
                  option={chartOption}
                  data={chartResponse?.chartData ? Object.values(chartResponse.chartData).flat() : []}
                  height={chart.height - 200}
                  loading={isLoading}
                  loadingText="Loading telemetry data..."
                  renderer="canvas"
                  notMerge={true}
                  lazyUpdate={false}
                  autoResize={true}
                />
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <WalteroTypography color="secondary">
                    {chart.selectedSensors.length === 0 ? 'Select sensors to display data' :
                     chart.selectedMeasures.length === 0 ? 'Select measures to display data' :
                     'No data available for the selected configuration'}
                  </WalteroTypography>
                </Box>
              )}
              
              {chartResponse.metadata && (
                <WalteroTypography variant="caption" color="secondary" sx={{ mt: 1, display: 'block' }}>
                  {chartResponse.metadata.totalRecords} data points • 
                  Last updated: {new Date().toLocaleTimeString()}
                </WalteroTypography>
              )}
            </>
          )}
          
          {!chart.selectedSensors.length && !chart.selectedMeasures.length && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <WalteroTypography color="secondary">
                Configure sensors and measures to start visualizing data
              </WalteroTypography>
            </Box>
          )}
        </CardContent>
        
        {/* Resize Handle */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 20,
            height: 20,
            cursor: 'nw-resize',
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderTopLeftRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.2)'
            }
          }}
        >
          <ResizeIcon sx={{ fontSize: 14, color: 'rgba(0,0,0,0.5)' }} />
        </Box>
      </Box>
    </Grid>
  );
}

export default TelemetryTab; 